/**
 * 图库仓储（T11b）：CRUD + 按时间倒序分页 + 按 favorite 过滤 + 清空。
 *
 * 表结构见 `src/core/db.ts` 的 `gallery`（索引 id / createdAt / favorite）。
 * 说明：IndexedDB 不支持 boolean 作为索引键，`favorite` 索引仅为清单/迁移声明；
 * 运行时按 favorite 过滤在内存中完成（本地图库数据量小，足够且行为可预期）。
 * 排序规则与游标分页沿用 `src/core/repositories/pagination.ts` 的约定：
 * createdAt 倒序、同刻以 id 兜底，保证「翻页不重不漏」。
 */
import type { Table } from 'dexie';

import { getDb } from '../../core/db';
import { newId } from '../../core/ids';
import { getMomentAccountId } from '../moments/accountScope';
import {
  decodeCursor,
  encodeCursor,
  normalizeLimit,
  type Page,
  type PageQuery,
} from '../../core/repositories/pagination';

export interface GalleryImage {
  id: string;
  dataUrl: string;
  mime: string;
  prompt: string;
  revisedPrompt?: string;
  model: string;
  createdAt: number;
  favorite: boolean;
  presetId?: string;
  conversationId?: string;
  userId?: string;
}

export interface CreateGalleryImageInput {
  id?: string;
  dataUrl: string;
  mime: string;
  prompt: string;
  revisedPrompt?: string;
  model: string;
  createdAt?: number;
  favorite?: boolean;
  presetId?: string;
  conversationId?: string;
  userId?: string;
}

export interface GalleryQuery extends PageQuery {
  /** 仅取收藏 / 未收藏；不传则不过滤。 */
  favorite?: boolean;
  presetId?: string;
  conversationId?: string;
  empty?: boolean;
}

function table(): Table<GalleryImage, string> {
  return getDb().gallery;
}

/** 纯构造：只归一化字段，不落库（便于单测与调用方复用）。 */
export function createGalleryImage(input: CreateGalleryImageInput): GalleryImage {
  return {
    id: input.id ?? newId(),
    dataUrl: input.dataUrl,
    mime: input.mime,
    prompt: input.prompt,
    model: input.model,
    createdAt: input.createdAt ?? Date.now(),
    favorite: input.favorite ?? false,
    ...(input.revisedPrompt === undefined ? {} : { revisedPrompt: input.revisedPrompt }),
    ...(input.presetId === undefined ? {} : { presetId: input.presetId }),
    ...(input.conversationId === undefined ? {} : { conversationId: input.conversationId }),
    ...(input.userId === undefined ? {} : { userId: input.userId }),
  };
}

export async function putGalleryImage(image: GalleryImage): Promise<string> {
  return table().put(image);
}

export async function createAndSaveGalleryImage(
  input: CreateGalleryImageInput,
): Promise<GalleryImage> {
  const image = createGalleryImage(input);
  await putGalleryImage(image);
  return image;
}

export async function getGalleryImage(id: string): Promise<GalleryImage | undefined> {
  return table().get(id);
}

/** 比较器：createdAt 倒序；同刻用 id 兜底，构成稳定全序。 */
export function compareGalleryImages(a: GalleryImage, b: GalleryImage): number {
  if (a.createdAt !== b.createdAt) return b.createdAt - a.createdAt;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** 列出（可按 favorite 过滤），已按时间倒序排好序。 */
export async function listGalleryImages(query: GalleryQuery = {}): Promise<GalleryImage[]> {
  if (query.empty) return [];
  const db = getDb();
  const userId = getMomentAccountId();
  // Both local automation and cloud deliveries store generated Moment images here.
  // A durable receipt prevents a deleted gallery image from returning on the next visit.
  await db.transaction('rw', db.gallery, db.moments, async () => {
    const moments = await db.moments.toArray();
    const existing = await db.gallery.toArray();
    for (const moment of moments) {
      if (moment.galleryImported || (moment.userId !== undefined && moment.userId !== userId)) continue;
      if (query.presetId !== undefined && moment.presetId !== query.presetId) continue;
      if (query.conversationId !== undefined && query.presetId === undefined) continue;
      for (const dataUrl of moment.images) {
        if (existing.some(image => image.dataUrl === dataUrl && image.userId === moment.userId &&
          (image.presetId === undefined || image.presetId === moment.presetId))) continue;
        const image = createGalleryImage({ dataUrl, mime: /^data:([^;,]+)/.exec(dataUrl)?.[1] ?? 'image/png',
          prompt: moment.content, model: 'moment-image', createdAt: moment.createdAt, presetId: moment.presetId,
          ...(moment.userId === undefined ? {} : { userId: moment.userId }) });
        await db.gallery.put(image);
        existing.push(image);
      }
      await db.moments.update(moment.id, { galleryImported: true });
    }
  });
  const all = (await table().toArray()).filter(image => image.userId === undefined || image.userId === userId);
  const favoriteMatches = (image: GalleryImage): boolean => query.favorite === undefined || image.favorite === query.favorite;
  if (query.presetId === undefined && query.conversationId === undefined) return all.filter(favoriteMatches).sort(compareGalleryImages);

  const [conversations, messages, moments] = await Promise.all([
    db.conversations.toArray(), db.messages.toArray(), db.moments.toArray(),
  ]);
  const conversationIds = new Set(conversations.filter(conversation => query.presetId !== undefined
    ? conversation.presetId === query.presetId : conversation.id === query.conversationId).map(row => row.id));
  const historicalUrls = new Set(messages.filter(message => message.role === 'assistant' &&
    conversationIds.has(message.conversationId) &&
    (message.backgroundJob === undefined || message.backgroundJob.userId === userId))
    .flatMap(message => message.images ?? []));
  if (query.presetId !== undefined) {
    for (const moment of moments) {
      if (moment.presetId === query.presetId && (moment.userId === undefined || moment.userId === userId)) {
        for (const dataUrl of moment.images) historicalUrls.add(dataUrl);
      }
    }
  }
  return all.filter(image => {
    if (!favoriteMatches(image)) return false;
    if (query.presetId !== undefined) {
      if (image.presetId !== undefined) return image.presetId === query.presetId;
      if (image.conversationId !== undefined) return conversationIds.has(image.conversationId);
      return historicalUrls.has(image.dataUrl);
    }
    if (image.conversationId !== undefined) return image.conversationId === query.conversationId;
    return image.presetId === undefined && historicalUrls.has(image.dataUrl);
  }).sort(compareGalleryImages);
}

/**
 * 游标分页：排序规则与 `listGalleryImages` 一致。
 * 游标记录上一页最后一条的 (createdAt, id)，下一页只取严格排在其后的记录。
 */
export async function listGalleryImagesPage(query: GalleryQuery = {}): Promise<Page<GalleryImage>> {
  const limit = normalizeLimit(query.limit);
  const ordered = await listGalleryImages(query);
  const cursor = query.cursor ? decodeCursor(query.cursor) : null;
  const afterCursor =
    cursor === null
      ? ordered
      : ordered.filter((image) => {
          const pivot: GalleryImage = {
            id: cursor.id,
            dataUrl: '',
            mime: '',
            prompt: '',
            model: '',
            createdAt: cursor.createdAt,
            favorite: false,
          };
          return compareGalleryImages(image, pivot) > 0;
        });

  const items = afterCursor.slice(0, limit);
  const hasMore = afterCursor.length > limit;
  const last = items[items.length - 1];
  return {
    items,
    hasMore,
    nextCursor: hasMore && last ? encodeCursor(last.createdAt, last.id) : null,
  };
}

/** 设置收藏状态；记录不存在返回 undefined。 */
export async function setGalleryFavorite(
  id: string,
  favorite: boolean,
): Promise<GalleryImage | undefined> {
  const existing = await getGalleryImage(id);
  if (existing === undefined) return undefined;
  const next: GalleryImage = { ...existing, favorite };
  await putGalleryImage(next);
  return next;
}

/** 切换收藏状态；返回更新后的记录（不存在则 undefined）。 */
export async function toggleGalleryFavorite(id: string): Promise<GalleryImage | undefined> {
  const existing = await getGalleryImage(id);
  if (existing === undefined) return undefined;
  return setGalleryFavorite(id, !existing.favorite);
}

export async function deleteGalleryImage(id: string): Promise<void> {
  await table().delete(id);
}

export async function countGalleryImages(): Promise<number> {
  return table().count();
}

export async function clearGallery(query: GalleryQuery = {}): Promise<void> {
  const images = await listGalleryImages(query);
  await table().bulkDelete(images.map(image => image.id));
}
